import React, { useState, useEffect } from 'react';
import { catalogAPI, accountAPI } from '../services/api';
import { Upload, Link, Search, Package, Trash2, Filter } from 'lucide-react';
import Pagination from '../components/Pagination';
import toast from 'react-hot-toast';

const PER_PAGE = 30;

export default function CatalogPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [availableOnly, setAvailableOnly] = useState(false);
  const [loading, setLoading] = useState(true);


  const [showImport, setShowImport] = useState(false);
  const [ymlUrl, setYmlUrl] = useState('');
  const [saveForAutoSync, setSaveForAutoSync] = useState(true);
  const [savedYmlUrl, setSavedYmlUrl] = useState('');
  const [ymlFile, setYmlFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const loadProducts = (p = page) => {
    catalogAPI.products((p - 1) * PER_PAGE, PER_PAGE, filterCat || null, search || null, availableOnly)
      .then(res => { setProducts(res.data.items); setTotal(res.data.total); })
      .catch(() => {});
  };

  const loadAll = () => {
    Promise.all([
      catalogAPI.products(0, PER_PAGE, null, null, false),
      catalogAPI.categories(),
      catalogAPI.stats(),
      accountAPI.getPixelId(),
    ]).then(([pRes, cRes, sRes, aRes]) => {
      setProducts(pRes.data.items); setTotal(pRes.data.total);
      setCategories(cRes.data);
      setStats(sRes.data);
      const url = aRes.data.yml_url || '';
      setSavedYmlUrl(url);
      if (url && !ymlUrl) setYmlUrl(url);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadProducts(page); }, [page]);
  useEffect(() => { loadProducts(1); setPage(1); }, [filterCat, availableOnly]);

  const handleSearch = (e) => {
    e.preventDefault();
    loadProducts(1); setPage(1);
  };

  const handleImportUrl = async () => {
    if (!ymlUrl) { toast.error('Введите URL'); return; }
    setImporting(true); setImportResult(null);
    try {
      const res = await catalogAPI.importYmlUrl(ymlUrl);
      setImportResult(res.data);
      toast.success(`Импорт: ${res.data.products_created} новых, ${res.data.products_updated} обновлено`);


      if (saveForAutoSync && ymlUrl !== savedYmlUrl) {
        try {
          await accountAPI.update({ yml_url: ymlUrl });
          setSavedYmlUrl(ymlUrl);
          toast.success('URL сохранён — автосинк каждые 6 часов');
        } catch {}
      }

      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка импорта'); }
    finally { setImporting(false); }
  };

  const handleDisableAutoSync = async () => {
    try {
      await accountAPI.update({ yml_url: null });
      setSavedYmlUrl('');
      toast.success('Автосинк отключён');
    } catch { toast.error('Ошибка'); }
  };

  const handleImportFile = async () => {
    if (!ymlFile) { toast.error('Выберите файл'); return; }
    setImporting(true); setImportResult(null);
    try {
      const res = await catalogAPI.importYmlFile(ymlFile);
      setImportResult(res.data);
      toast.success(`Импорт: ${res.data.products_created} новых, ${res.data.products_updated} обновлено`);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка импорта'); }
    finally { setImporting(false); }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Удалить весь каталог? Это действие необратимо.')) return;
    try { await catalogAPI.deleteAll(); toast.success('Каталог удалён'); loadAll(); }
    catch { toast.error('Ошибка'); }
  };

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Загрузка...</div>;

  return (
    <div className="animate-in">
      <div className="page-header">
        <h1 className="page-title">Каталог товаров</h1>
        <p className="page-subtitle">{total} товаров · {categories.length} категорий</p>
      </div>

      <div className="page-actions">
        <button className="btn btn-primary" onClick={() => setShowImport(!showImport)}>
          <Upload size={16} /> Импорт YML
        </button>
        {total > 0 && (
          <button className="btn btn-danger btn-sm" onClick={handleDeleteAll}>
            <Trash2 size={14} /> Очистить
          </button>
        )}
      </div>


      {showImport && (
        <div className="card" style={{ marginBottom: 24, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Импорт каталога из YML</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
                <Link size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> По URL
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="form-input" placeholder="https://shop.ru/feed.yml" style={{ flex: 1 }}
                  value={ymlUrl} onChange={e => setYmlUrl(e.target.value)} />
                <button className="btn btn-primary btn-sm" onClick={handleImportUrl} disabled={importing}>
                  {importing ? '...' : 'Загрузить'}
                </button>
              </div>
              <label className="form-check" style={{ fontSize: 11, marginTop: 8, color: 'var(--text-muted)' }}>
                <input type="checkbox" checked={saveForAutoSync} onChange={e => setSaveForAutoSync(e.target.checked)} />
                Автоматически синхронизировать каждые 6 часов
              </label>
              {savedYmlUrl && (
                <div style={{ fontSize: 11, marginTop: 6, color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  ✓ Автосинк включён
                  <button type="button" onClick={handleDisableAutoSync}
                    style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 11, padding: 0 }}>
                    отключить
                  </button>
                </div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
                <Upload size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Из файла
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="file" accept=".xml,.yml" style={{ fontSize: 12, color: 'var(--text-secondary)' }}
                  onChange={e => setYmlFile(e.target.files[0])} />
                <button className="btn btn-primary btn-sm" onClick={handleImportFile} disabled={importing || !ymlFile}>
                  {importing ? '...' : 'Загрузить'}
                </button>
              </div>
            </div>
          </div>

          {importResult && (
            <div style={{ marginTop: 16, padding: 14, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', fontSize: 13, lineHeight: 1.8 }}>
              <div style={{ color: 'var(--success)' }}>Категорий: <strong>+{importResult.categories_created}</strong> новых, {importResult.categories_updated} обновлено</div>
              <div style={{ color: 'var(--success)' }}>Товаров: <strong>+{importResult.products_created}</strong> новых, {importResult.products_updated} обновлено</div>
              {importResult.products_deactivated > 0 && (
                <div style={{ color: 'var(--warning)' }}>Деактивировано: {importResult.products_deactivated} (нет в фиде)</div>
              )}
              {importResult.errors?.length > 0 && importResult.errors.map((e, i) => (
                <div key={i} style={{ fontSize: 11, color: 'var(--danger)' }}>{e}</div>
              ))}
            </div>
          )}
        </div>
      )}


      {stats && stats.total_products > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
          <div className="stat-card">
            <div className="stat-card-label">Всего</div>
            <div className="stat-card-value">{stats.total_products}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">В наличии</div>
            <div className="stat-card-value" style={{ color: 'var(--success)' }}>{stats.available_products}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">Категорий</div>
            <div className="stat-card-value">{stats.total_categories}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">Ср. цена</div>
            <div className="stat-card-value">{stats.avg_price?.toLocaleString('ru')} ₽</div>
          </div>
        </div>
      )}


      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, flex: 1, minWidth: 200 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input className="form-input" style={{ paddingLeft: 32 }} placeholder="Поиск товаров..."
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button type="submit" className="btn btn-ghost btn-sm">Найти</button>
        </form>
        <select className="form-input" style={{ width: 200 }} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
          <option value="">Все категории</option>
          {categories.map(c => <option key={c.external_id} value={c.external_id}>{c.name}</option>)}
        </select>
        <label className="form-check" style={{ fontSize: 12 }}>
          <input type="checkbox" checked={availableOnly} onChange={e => setAvailableOnly(e.target.checked)} />
          Только в наличии
        </label>
      </div>


      <div className="table-container" style={{ overflowX: 'auto' }}>
        <table className="table" style={{ minWidth: 800, fontSize: 12 }}>
          <thead>
            <tr><th style={{ width: 50 }}></th><th>ID</th><th>Название</th><th>Категория</th><th>Цена</th><th>Бренд</th><th>Статус</th></tr>
          </thead>
          <tbody>
            {products.map(p => (
              <tr key={p.id}>
                <td>
                  {p.picture ? (
                    <img src={p.picture} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, background: 'var(--bg-secondary)' }} />
                  ) : (
                    <div style={{ width: 36, height: 36, borderRadius: 4, background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Package size={14} style={{ color: 'var(--text-muted)' }} />
                    </div>
                  )}
                </td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{p.external_id}</td>
                <td style={{ fontWeight: 500, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={p.name}>{p.name}</td>
                <td style={{ color: 'var(--text-muted)' }}>{p.category_name || '—'}</td>
                <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                  {p.price ? `${p.price.toLocaleString('ru')} ₽` : '—'}
                  {p.old_price && (
                    <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)', marginLeft: 6, fontWeight: 400, fontSize: 10 }}>
                      {p.old_price.toLocaleString('ru')}
                    </span>
                  )}
                </td>
                <td style={{ color: 'var(--text-muted)' }}>{p.brand || p.vendor || '—'}</td>
                <td>
                  <span className={`badge ${p.available ? 'badge-success' : 'badge-danger'}`}>
                    {p.available ? 'В наличии' : 'Нет'}
                  </span>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                {total === 0 ? 'Каталог пуст. Импортируйте YML-фид.' : 'Ничего не найдено'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={Math.ceil(total / PER_PAGE)} onPageChange={setPage} />
    </div>
  );
}